import React from 'react';
import PropTypes from 'prop-types';
import { useAuth } from '../hooks/useAuth';
import LoginPage from '../pages/LoginPage';
import { Skeleton } from './ui';

/**
 * AuthWrapper Component
 * 
 * Protected route wrapper - redirects to login if not authenticated.
 * Shows loading state while checking authentication.
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render if authenticated
 * @param {React.ReactNode} props.loadingFallback - Custom loading fallback
 * @returns {JSX.Element} Auth wrapper component
 */
export default function AuthWrapper({ 
  children, 
  loadingFallback = <AuthLoadingScreen /> 
}) {
  const { isAuthenticated, isLoading } = useAuth();

  // Show loading state while checking authentication
  if (isLoading) {
    return loadingFallback;
  }

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return <LoginPage />;
  }

  // Render children if authenticated
  return children;
}

AuthWrapper.propTypes = {
  children: PropTypes.node.isRequired,
  loadingFallback: PropTypes.node,
};

/**
 * AuthLoadingScreen Component
 * 
 * Default loading screen for authentication check.
 */
function AuthLoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center space-y-4">
        <div className="mx-auto w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        <p className="text-muted-foreground">იტვირთება...</p>
      </div>
    </div>
  );
}

/**
 * AuthLoadingSkeleton Component
 * 
 * Skeleton loader for authenticated app loading.
 */
export function AuthLoadingSkeleton() {
  return (
    <div className="min-h-screen bg-background p-6">
      <div className="container mx-auto space-y-6">
        {/* Header Skeleton */}
        <div className="flex justify-between items-center">
          <Skeleton className="h-10 w-32" />
          <Skeleton className="h-10 w-32" />
        </div>

        {/* Dashboard Stats Skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-32 w-full" />
          ))}
        </div>

        {/* Account Limits Skeleton */}
        <Skeleton className="h-48 w-full" />

        {/* Table Skeleton */}
        <Skeleton className="h-96 w-full" />
      </div>
    </div>
  );
}
